import React, { useEffect, useRef, useState } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from 'recharts';
import './Dashboard.css';

const colors = ['#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF'];

const Dashboard = () => {
    const [messagesData, setMessagesData] = useState([]);
    const [botResponsesData, setBotResponsesData] = useState([]);
    const [senderBreakdown, setSenderBreakdown] = useState([]);
    const [categoryBreakdown, setCategoryBreakdown] = useState([]);
    const [gptUsage, setGptUsage] = useState({ total_tokens: 0, total_cost: 0 });
    const [gptLogData, setGptLogData] = useState([]);
    const [logs, setLogs] = useState([]);
    const [showLogs, setShowLogs] = useState(false);
    const logsEndRef = useRef(null);

    useEffect(() => {
        fetch('https://adam.app.autofront.com.au/api/stats/messages-per-day')
            .then(res => res.json()).then(setMessagesData);

        fetch('https://adam.app.autofront.com.au/api/stats/bot-responses-per-day')
            .then(res => res.json()).then(setBotResponsesData);

        fetch('https://adam.app.autofront.com.au/api/stats/sender-breakdown')
            .then(res => res.json())
            .then(data =>
                setSenderBreakdown(Object.entries(data).map(([name, value]) => ({ name, value })))
            );

        fetch('https://adam.app.autofront.com.au/api/stats/category-breakdown')
            .then(res => res.json())
            .then(data =>
                setCategoryBreakdown(Object.entries(data).map(([name, value]) => ({ name, value })))
            );

        fetch('https://adam.app.autofront.com.au/api/stats/gpt-usage-summary')
            .then(res => res.json())
            .then(setGptUsage);

        fetch('https://adam.app.autofront.com.au/api/stats/gpt-usage-log')
            .then(res => res.json())
            .then(setGptLogData);
    }, []);

    useEffect(() => {
        let interval;
        if (showLogs) {
            const fetchLogs = () => {
                fetch('https://adam.app.autofront.com.au/api/logs')
                    .then(res => res.json())
                    .then(setLogs)
                    .catch(console.error);
            };
            fetchLogs();
            interval = setInterval(fetchLogs, 3000);
        }
        return () => clearInterval(interval);
    }, [showLogs]);

    useEffect(() => {
        if (logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs]);

    const renderLineChart = (data, title) => (
        <div className="dashboard-card">
            <h4>{title}</h4>
            <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#36A2EB"
                        strokeWidth={3}
                        dot={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );

    const renderPieChart = (data, title) => (
        <div className="dashboard-card">
            <h4>{title}</h4>
            <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                    <Pie
                        data={data}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={70}
                        label
                    >
                        {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                        ))}
                    </Pie>
                    <Legend verticalAlign="bottom" height={36} />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );

    const renderGptUsageCard = () => (
        <div className="dashboard-card gpt-usage-card">
            <h4>GPT Usage</h4>
            <div className="gpt-metrics">
                <div className="gpt-metric">
                    <div className="gpt-value">{gptUsage.total_tokens?.toLocaleString() || '0'}</div>
                    <div className="gpt-label">Tokens</div>
                </div>
                <div className="gpt-metric">
                    <div className="gpt-value">${gptUsage.total_cost?.toFixed(4) || '0.0000'}</div>
                    <div className="gpt-label">Estimated Cost (USD)</div>
                </div>
            </div>
        </div>
    );

    const renderGptLogLineChart = () => (
        <div className="dashboard-card">
            <h4>GPT Token Usage Events</h4>
            <ResponsiveContainer width="100%" height={200}>
                <LineChart data={gptLogData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timestamp" tickFormatter={ts => new Date(ts).toLocaleTimeString()} />
                    <YAxis />
                    <Tooltip />
                    <Line
                        type="monotone"
                        dataKey="tokens"
                        stroke="#FF6384"
                        strokeWidth={2}
                        dot={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );

    const renderLiveLogConsole = () => (
        <div className="dashboard-card">
            <button onClick={() => setShowLogs(!showLogs)}>
                {showLogs ? 'Hide Console Logs' : 'Show Console Logs'}
            </button>
            {showLogs && (
                <div className="log-console">
                    <pre>
                        {logs.join('\n')}
                        <div ref={logsEndRef} />
                    </pre>
                </div>
            )}
        </div>
    );

    return (
        <div className="dashboard-grid">
            {renderLineChart(messagesData, 'Total Messages')}
            {renderLineChart(botResponsesData, 'Bot Responses')}
            {renderPieChart(senderBreakdown, 'Sender Breakdown')}
            {renderPieChart(categoryBreakdown, 'Category Breakdown')}
            {renderGptUsageCard()}
            {renderGptLogLineChart()}
            {renderLiveLogConsole()}
        </div>
    );
};

export default Dashboard;
